// One v1 server-sent event stream read with fetch (EventSource cannot send the bearer): each event's data is one JSON
// object on one line, and comment lines (": open", ": heartbeat") only keep it awake.
import { currentUserId } from '@/infrastructure/clients/BackendClient';
import { openChangeStream } from '@/infrastructure/api/v1/client';

/** No byte for this long (the server sends a heartbeat every 15 seconds) means the stream is dead. */
const SILENCE_MS = 45_000;

/** Delays before each reconnect after a stream ends, the last repeating. */
export const STREAM_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/**
 * Opens the stream at `path` (through the app's one way to the backend, which sends it to Frankfurt) and hands every event to `received` until it ends: `opened` runs once it is live (the
 * response headers arrived). 'signedOut' for a 401 once the session is gone; a 401 with the session kept (its refresh
 * failed for a reason that passes) is 'ended'. 'denied' for a 403 or 404, which may be lasting or a deploy's blip.
 * Otherwise 'ended'.
 */
export async function readEventStream<T>(
  path: string,
  controller: AbortController,
  opened: () => void,
  received: (event: T) => void,
  label: string,
): Promise<'signedOut' | 'denied' | 'ended'> {
  let silence: ReturnType<typeof setTimeout> | undefined;
  const awake = () => {
    clearTimeout(silence);
    silence = setTimeout(() => controller.abort(), SILENCE_MS);
  };
  try {
    const response = await openChangeStream(path, controller.signal);
    if (response.status === 401) return currentUserId() ? 'ended' : 'signedOut';
    if (response.status === 403 || response.status === 404) return 'denied';
    if (!response.ok || !response.body) return 'ended';
    opened();
    awake();
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return 'ended';
      awake();
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let end = buffer.indexOf('\n\n');
      while (end >= 0) {
        const frame = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const data = frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
        if (data) {
          try {
            received(JSON.parse(data) as T);
          } catch (error) {
            console.warn(`[${label}] unreadable event`, error);
          }
        }
        end = buffer.indexOf('\n\n');
      }
    }
  } catch {
    return 'ended';
  } finally {
    clearTimeout(silence);
  }
}
